import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Zap, ArrowRight, X, Sparkles } from 'lucide-react';
import { KITS } from '../data/kits';
import { UPSELLS } from '../data/upsells';
import { PaymentMethods } from './PaymentMethods';
interface CartSummaryProps {
  selectedKitIds: string[];
  selectedUpsellIds: string[];
  onRemoveKit: (id: string) => void;
  onRemoveUpsell: (id: string) => void;
  onCheckoutAttempt: () => void;
}
export function CartSummary({
  selectedKitIds,
  selectedUpsellIds,
  onRemoveKit,
  onRemoveUpsell,
  onCheckoutAttempt
}: CartSummaryProps) {
  const selectedKits = KITS.filter(k => selectedKitIds.includes(k.id));
  const selectedUpsells = UPSELLS.filter(u => selectedUpsellIds.includes(u.id));
  const kitsPrice = selectedKits.reduce((acc, k) => acc + k.price, 0);
  const kitsOldPrice = selectedKits.reduce((acc, k) => acc + k.oldPrice, 0);
  const upsellsPrice = selectedUpsells.reduce((acc, u) => acc + u.price, 0);
  const upsellsOldPrice = selectedUpsells.reduce((acc, u) => acc + u.oldPrice, 0);
  const total = kitsPrice + upsellsPrice;
  const oldTotal = kitsOldPrice + upsellsOldPrice;
  const savings = oldTotal - total;
  const hasItems = selectedKits.length > 0 || selectedUpsells.length > 0;
  const handleCheckout = () => {
    onCheckoutAttempt();
  };
  return <section className="py-24 bg-cream" data-id="element-157">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-158">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="bg-white rounded-[2rem] shadow-premium border border-ink/5 overflow-hidden" data-id="element-159">
          <div className="bg-ink text-white p-6 text-center" data-id="element-160">
            <h2 className="font-bold text-2xl tracking-tight" data-id="element-161">Tu selección</h2>
            {hasItems && <p className="text-white/70 text-sm mt-1" data-id="element-162">
                {selectedKits.length}{' '}
                {selectedKits.length === 1 ? 'kit principal' : 'kits principales'}
                {selectedUpsells.length > 0 && ` · ${selectedUpsells.length} ${selectedUpsells.length === 1 ? 'complemento' : 'complementos'}`}
              </p>}
          </div>
          <div className="p-6 md:p-8" data-id="element-163">
            {!hasItems ? <div className="text-center py-8 text-ink-light" data-id="element-164">
                <p className="mb-4" data-id="element-165">Aún no elegiste ningún kit principal.</p>
                <a href="#kits" className="text-combo-green font-bold hover:underline" data-id="element-166">
                  Elegir un kit ahora &rarr;
                </a>
              </div> : <div className="space-y-4" data-id="element-167">
                {/* Main Kits */}
                <AnimatePresence initial={false} data-id="element-168">
                  {selectedKits.map(kit => <motion.div key={kit.id} initial={{
                opacity: 0,
                height: 0,
                overflow: 'hidden'
              }} animate={{
                opacity: 1,
                height: 'auto',
                overflow: 'visible'
              }} exit={{
                opacity: 0,
                height: 0,
                overflow: 'hidden'
              }} className="flex justify-between items-center pb-4 border-b border-ink/5" data-id="element-169">
                      <div className="flex items-center gap-3" data-id="element-170">
                        <button onClick={() => onRemoveKit(kit.id)} className="text-ink-light hover:text-red-500 transition-colors" aria-label={`Quitar ${kit.name}`} data-id="element-171">
                          <X className="w-4 h-4" data-id="element-172" />
                        </button>
                        <div className={`w-2 h-10 rounded-full ${kit.color}`} aria-hidden="true" data-id="element-173" />
                        <div data-id="element-174">
                          <div className="font-bold text-ink" data-id="element-175">{kit.name}</div>
                          <div className="text-xs text-ink-light" data-id="element-176">
                            Kit principal · Acceso de por vida
                          </div>
                        </div>
                      </div>
                      <div className="text-right" data-id="element-177">
                        <div className="font-bold text-ink" data-id="element-178">
                          ${kit.price.toLocaleString('es-AR')}
                        </div>
                        <div className="text-xs text-ink-light line-through" data-id="element-179">
                          ${kit.oldPrice.toLocaleString('es-AR')}
                        </div>
                      </div>
                    </motion.div>)}
                  {/* Upsells */}
                  {selectedUpsells.map(upsell => <motion.div key={upsell.id} initial={{
                opacity: 0,
                height: 0,
                overflow: 'hidden'
              }} animate={{
                opacity: 1,
                height: 'auto',
                overflow: 'visible'
              }} exit={{
                opacity: 0,
                height: 0,
                overflow: 'hidden'
              }} className="flex justify-between items-center pb-4 border-b border-ink/5" data-id="element-180">
                      <div className="flex items-center gap-3" data-id="element-181">
                        <button onClick={() => onRemoveUpsell(upsell.id)} className="text-ink-light hover:text-red-500 transition-colors" aria-label={`Quitar ${upsell.name}`} data-id="element-182">
                          <X className="w-4 h-4" data-id="element-183" />
                        </button>
                        <div className="w-2 h-10 rounded-full" style={{
                    backgroundColor: upsell.color
                  }} aria-hidden="true" data-id="element-184" />
                        <div data-id="element-185">
                          <div className="font-medium text-sm text-ink" data-id="element-186">
                            {upsell.name}
                          </div>
                          <div className="text-xs text-ink-light" data-id="element-187">
                            Complemento
                          </div>
                        </div>
                      </div>
                      <div className="text-right" data-id="element-188">
                        <div className="font-medium text-sm text-ink" data-id="element-189">
                          ${upsell.price.toLocaleString('es-AR')}
                        </div>
                        <div className="text-xs text-ink-light line-through" data-id="element-190">
                          ${upsell.oldPrice.toLocaleString('es-AR')}
                        </div>
                      </div>
                    </motion.div>)}
                </AnimatePresence>
                {/* Savings line */}
                {savings > 0 && <div className="flex justify-between items-center text-sm font-bold text-combo-green bg-combo-green/5 rounded-lg px-3 py-2" data-id="element-191">
                    <span data-id="element-192">Estás ahorrando</span>
                    <span data-id="element-193">${savings.toLocaleString('es-AR')}</span>
                  </div>}
                {/* Total */}
                <div className="pt-4 flex justify-between items-end" data-id="element-194">
                  <div className="text-ink-light font-medium" data-id="element-195">
                    Total a pagar:
                  </div>
                  <div className="text-right" data-id="element-196">
                    <div className="text-xs text-ink-light line-through" data-id="element-197">
                      ${oldTotal.toLocaleString('es-AR')}
                    </div>
                    <motion.div key={total} initial={{
                  scale: 1.1,
                  color: '#1B4332'
                }} animate={{
                  scale: 1,
                  color: '#1A1A1A'
                }} className="text-3xl font-bold text-ink" data-id="element-198">
                      ${total.toLocaleString('es-AR')}
                    </motion.div>
                  </div>
                </div>
                {/* CTA */}
                <button onClick={handleCheckout} className="w-full mt-8 bg-combo-green text-white py-4 rounded-full font-bold text-lg hover:bg-combo-green/90 transition-all shadow-glow hover:shadow-[0_0_25px_rgba(27,67,50,0.6)] flex items-center justify-center gap-2 animate-pulse-glow" data-id="element-199">
                  CONFIRMAR MI COMPRA <ArrowRight className="w-5 h-5" data-id="element-200" />
                </button>
                {/* Trust Badges */}
                <div className="mt-6 flex flex-wrap justify-center gap-6 text-xs text-ink-light font-medium" data-id="element-201">
                  <div className="flex items-center gap-1.5" data-id="element-202">
                    <Lock className="w-4 h-4 text-combo-green" data-id="element-203" /> Pago 100%
                    Seguro
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-204">
                    <Zap className="w-4 h-4 text-gold" data-id="element-205" /> Acceso Inmediato
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-206">
                    <Sparkles className="w-4 h-4 text-combo-navy" data-id="element-207" /> Acceso de
                    por vida
                  </div>
                </div>
                {/* Payment methods */}
                <PaymentMethods className="mt-6 pt-6 border-t border-ink/5" data-id="element-208" />
              </div>}
          </div>
        </motion.div>
      </div>
    </section>;
}